// Cloudflare-backed sync adapter. The exported names are kept for the rest of
// the app, which historically called this module "pocketbase".
import { db } from '../db.js';
import { authManager } from './auth.js';

async function request(path, options = {}) {
    const response = await fetch(path, {
        credentials: 'include',
        headers: {
            'content-type': 'application/json',
            ...(options.headers || {}),
        },
        ...options,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || `Request failed with status ${response.status}`);
    }
    return data;
}

function asObject(items, keyFn) {
    const out = {};
    for (const item of items || []) {
        const key = keyFn(item);
        if (key) out[key] = item;
    }
    return out;
}

function mergeObjectByTimestamp(remote = {}, local = {}, keyName = 'id') {
    const merged = { ...remote };
    for (const item of Object.values(local || {})) {
        const key = item?.[keyName] || item?.id || item?.uuid;
        if (!key) continue;
        const existing = merged[key];
        if (!existing || (item.updatedAt || item.addedAt || 0) >= (existing.updatedAt || existing.addedAt || 0)) {
            merged[key] = item;
        }
    }
    return merged;
}

function mergeHistory(remote = [], local = []) {
    const combined = [...(remote || []), ...(local || [])].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    const seen = new Set();
    const result = [];
    for (const item of combined) {
        const key = item.timestamp || `${item.id}:${item.title}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(item);
        if (result.length >= 100) break;
    }
    return result;
}

const pb = {
    autoCancellation() {},
    collection() {
        throw new Error('PocketBase collections are not available with the Cloudflare sync backend.');
    },
};

const syncManager = {
    pb,
    _userRecordCache: null,
    _isSyncing: false,

    safeParseInternal(value, _fieldName, fallback) {
        if (!value) return fallback;
        if (typeof value !== 'string') return value;
        try {
            return JSON.parse(value);
        } catch {
            return fallback;
        }
    },

    _minifyItem(type, item) {
        return db._minifyItem(type, item);
    },

    async _getUserRecord(uid) {
        if (!uid || !authManager.user) return null;
        if (this._userRecordCache?.firebase_id === uid) return this._userRecordCache;
        const data = await this.getUserData();
        this._userRecordCache = {
            id: uid,
            firebase_id: uid,
            username: data?.profile?.username,
            display_name: data?.profile?.display_name,
            email: authManager.user.email,
        };
        return this._userRecordCache;
    },

    async getUserData() {
        if (!authManager.user) return null;
        return request('/api/sync/user');
    },

    async _patchUserData(data) {
        if (!authManager.user) return null;
        const updated = await request('/api/sync/user', {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
        this._userRecordCache = null;
        return updated;
    },

    async _updateUserJSON(_uid, field, data) {
        const map = {
            library: 'library',
            history: 'history',
            user_playlists: 'userPlaylists',
            user_folders: 'userFolders',
            profile: 'profile',
        };
        const apiField = map[field] || field;
        return this._patchUserData({ [apiField]: data });
    },

    async syncLibraryItem(type, item, added) {
        if (!authManager.user) return;
        const data = await this.getUserData();
        const library = data?.library || {};
        const pluralType = type === 'mix' ? 'mixes' : `${type}s`;
        const key = type === 'playlist' ? item.uuid || item.id : item.id;
        if (!library[pluralType]) library[pluralType] = {};
        if (added) library[pluralType][key] = this._minifyItem(type, item);
        else delete library[pluralType][key];
        await this._patchUserData({ library });
    },

    async syncHistoryItem(historyEntry) {
        if (!authManager.user) return;
        const data = await this.getUserData();
        const history = mergeHistory([historyEntry], data?.history || []);
        await this._patchUserData({ history });
    },

    async clearHistory() {
        if (!authManager.user) return;
        await this._patchUserData({ history: [] });
    },

    async syncUserPlaylist(playlist, action) {
        if (!authManager.user) return;
        const data = await this.getUserData();
        const userPlaylists = data?.userPlaylists || {};
        if (action === 'delete') {
            delete userPlaylists[playlist.id];
            await this.unpublishPlaylist(playlist.id);
        } else {
            userPlaylists[playlist.id] = {
                id: playlist.id,
                name: playlist.name,
                cover: playlist.cover || null,
                tracks: playlist.tracks ? playlist.tracks.map((t) => this._minifyItem(t.type || 'track', t)) : [],
                createdAt: playlist.createdAt || Date.now(),
                updatedAt: playlist.updatedAt || Date.now(),
                numberOfTracks: playlist.tracks ? playlist.tracks.length : 0,
                images: playlist.images || [],
                isPublic: playlist.isPublic || false,
                description: playlist.description || '',
            };
            if (playlist.isPublic) await this.publishPlaylist(playlist);
        }
        await this._patchUserData({ userPlaylists });
    },

    async syncUserFolder(folder, action) {
        if (!authManager.user) return;
        const data = await this.getUserData();
        const userFolders = data?.userFolders || {};
        if (action === 'delete') {
            delete userFolders[folder.id];
        } else {
            userFolders[folder.id] = {
                id: folder.id,
                name: folder.name,
                cover: folder.cover || null,
                playlists: folder.playlists || [],
                createdAt: folder.createdAt || Date.now(),
                updatedAt: folder.updatedAt || Date.now(),
            };
        }
        await this._patchUserData({ userFolders });
    },

    async getPublicPlaylist(uuid) {
        try {
            return await request(`/api/sync/public-playlists/${encodeURIComponent(uuid)}`);
        } catch (error) {
            if (/404|not found/i.test(error.message)) return null;
            throw error;
        }
    },

    async publishPlaylist(playlist) {
        if (!authManager.user || !playlist?.id) return;
        await request(`/api/sync/public-playlists/${encodeURIComponent(playlist.id)}`, {
            method: 'PUT',
            body: JSON.stringify({
                uuid: playlist.id,
                title: playlist.name,
                name: playlist.name,
                image: playlist.cover,
                cover: playlist.cover,
                description: playlist.description || '',
                tracks: playlist.tracks || [],
                data: {
                    title: playlist.name,
                    cover: playlist.cover,
                    description: playlist.description || '',
                },
            }),
        });
    },

    async unpublishPlaylist(uuid) {
        if (!authManager.user || !uuid) return;
        await request(`/api/sync/public-playlists/${encodeURIComponent(uuid)}`, {
            method: 'DELETE',
        });
    },

    async getProfile(username) {
        try {
            return await request(`/api/sync/profiles/${encodeURIComponent(username)}`);
        } catch {
            return null;
        }
    },

    async updateProfile(data) {
        if (!authManager.user) return;
        const current = await this.getUserData();
        await this._patchUserData({ profile: { ...(current?.profile || {}), ...data } });
    },

    async isUsernameTaken(username) {
        try {
            const data = await request(`/api/sync/usernames/${encodeURIComponent(username)}`);
            return !!data.taken;
        } catch {
            return false;
        }
    },

    async clearCloudData() {
        if (!authManager.user) return;
        await request('/api/sync/user', { method: 'DELETE' });
        this._userRecordCache = null;
        alert('Cloud data cleared successfully.');
    },

    async onAuthStateChanged(user) {
        if (!user) {
            this._userRecordCache = null;
            this._isSyncing = false;
            return;
        }
        if (this._isSyncing) return;
        this._isSyncing = true;

        try {
            const cloudData = await this.getUserData();
            if (!cloudData) return;

            const localData = await db.exportData();
            const localLibrary = {
                tracks: asObject(localData.favorites_tracks, (item) => item.id),
                albums: asObject(localData.favorites_albums, (item) => item.id),
                artists: asObject(localData.favorites_artists, (item) => item.id),
                playlists: asObject(localData.favorites_playlists, (item) => item.uuid || item.id),
                mixes: asObject(localData.favorites_mixes, (item) => item.id),
            };

            const library = {
                tracks: mergeObjectByTimestamp(cloudData.library?.tracks, localLibrary.tracks),
                albums: mergeObjectByTimestamp(cloudData.library?.albums, localLibrary.albums),
                artists: mergeObjectByTimestamp(cloudData.library?.artists, localLibrary.artists),
                playlists: mergeObjectByTimestamp(cloudData.library?.playlists, localLibrary.playlists, 'uuid'),
                mixes: mergeObjectByTimestamp(cloudData.library?.mixes, localLibrary.mixes),
            };
            const history = mergeHistory(cloudData.history, localData.history_tracks);
            const userPlaylists = mergeObjectByTimestamp(
                cloudData.userPlaylists,
                asObject(localData.user_playlists, (item) => item.id)
            );
            const userFolders = mergeObjectByTimestamp(
                cloudData.userFolders,
                asObject(localData.user_folders, (item) => item.id)
            );

            await this._patchUserData({ library, history, userPlaylists, userFolders });

            await db.importData(
                {
                    favorites_tracks: Object.values(library.tracks),
                    favorites_albums: Object.values(library.albums),
                    favorites_artists: Object.values(library.artists),
                    favorites_playlists: Object.values(library.playlists),
                    favorites_mixes: Object.values(library.mixes),
                    history_tracks: history,
                    user_playlists: Object.values(userPlaylists),
                    user_folders: Object.values(userFolders),
                },
                true
            );

            window.dispatchEvent(new CustomEvent('library-changed'));
            window.dispatchEvent(new CustomEvent('history-changed'));
            window.dispatchEvent(new HashChangeEvent('hashchange'));
            console.log('[Cloudflare Sync] Sync completed');
        } catch (error) {
            console.error('[Cloudflare Sync] Sync error:', error);
        } finally {
            this._isSyncing = false;
        }
    },
};

authManager.onAuthStateChanged(syncManager.onAuthStateChanged.bind(syncManager));

export { pb, syncManager };
